export const GAME_TYPE_API_KEYS = [
    "find_the_match",
    "game_show_quiz",
    "flip_cards",
    "air_plan/flying_fruits",
    "flappy_bird",
    "whack_a_mole",
    "anagram",
    "complete_the_sentence",
    "situation",
] as const;

export type GameTypeApiKey = (typeof GAME_TYPE_API_KEYS)[number];

export type GameTypeMessageKey =
    | "findTheMatch"
    | "gameShowQuiz"
    | "flipCards"
    | "airPlanFlyingFruits"
    | "flappyBird"
    | "whackAMole"
    | "anagram"
    | "completeTheSentence"
    | "situation"
    | "sequence";

const GAME_TYPE_KEY_TO_MESSAGE_KEY: Record<string, GameTypeMessageKey> = {
    find_the_match: "findTheMatch",
    game_show_quiz: "gameShowQuiz",
    flip_cards: "flipCards",
    "air_plan/flying_fruits": "airPlanFlyingFruits",
    flying_fruits: "airPlanFlyingFruits",
    flappy_bird: "flappyBird",
    whack_a_mole: "whackAMole",
    anagram: "anagram",
    complete_the_sentence: "completeTheSentence",
    situation: "situation",
    sequence: "sequence",
};

export const CANONICAL_GAME_TYPE_TITLES: Record<GameTypeApiKey, string> = {
    find_the_match: "Find the match",
    game_show_quiz: "Game show Quiz",
    flip_cards: "Flip cards",
    "air_plan/flying_fruits": "Flying Fruits",
    flappy_bird: "Flappy Chicken",
    whack_a_mole: "Whack a mole",
    anagram: "Anagram",
    complete_the_sentence: "Complete the Sentence",
    situation: "Situation",
};

export const GAME_TYPE_TITLE_TO_API_KEY: Record<string, GameTypeApiKey | "sequence"> = {
    "Find the match": "find_the_match",
    "Find the Match": "find_the_match",
    "Game show Quiz": "game_show_quiz",
    "Game Show Quiz": "game_show_quiz",
    "Flip cards": "flip_cards",
    "Flip Cards": "flip_cards",
    "Flying Fruits": "air_plan/flying_fruits",
    "Flappy Chicken": "flappy_bird",
    "Flappy chicken": "flappy_bird",
    "Whack a mole": "whack_a_mole",
    "Whack a Mole": "whack_a_mole",
    Anagram: "anagram",
    "Complete the Sentence": "complete_the_sentence",
    "Complete the sentence": "complete_the_sentence",
    Situation: "situation",
    Sequence: "sequence",
};

export function normalizeGameTypeKey(gameType: unknown): string {
    return String(gameType ?? "").trim().toLowerCase();
}

export function getGameTypeMessageKey(gameType: unknown): GameTypeMessageKey | null {
    const normalized = normalizeGameTypeKey(gameType);
    if (!normalized) return null;
    return GAME_TYPE_KEY_TO_MESSAGE_KEY[normalized] ?? null;
}

export function getGameTypeLabel(
    gameType: unknown,
    translate: (messageKey: GameTypeMessageKey) => string,
): string {
    const messageKey = getGameTypeMessageKey(gameType);
    if (!messageKey) return "-";
    return translate(messageKey);
}

export function getLibraryGameTypeDisplay(
    gameTypeKey: string,
    source: string,
    translate: (messageKey: GameTypeMessageKey) => string,
): string {
    if (gameTypeKey.trim()) {
        return getGameTypeLabel(gameTypeKey, translate);
    }

    if (normalizeGameTypeKey(source) === "sequence") {
        return translate("sequence");
    }

    return "-";
}

export function getCanonicalGameTypeTitle(gameType: unknown): string {
    const normalized = normalizeGameTypeKey(gameType) as GameTypeApiKey;
    return CANONICAL_GAME_TYPE_TITLES[normalized] ?? String(gameType ?? "");
}
